import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Shield, AlertTriangle, CheckCircle, ExternalLink, Clock, DollarSign } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VetBuyerDialog = ({ buyerInfo, listingId, open, onClose }) => {
  const [selectedProvider, setSelectedProvider] = useState("backgroundchecks");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const providers = [
    {
      id: "backgroundchecks",
      name: "BackgroundChecks.com",
      price: "$35",
      turnaround: "Minutes - 24hrs",
      features: [
        "Criminal records (nationwide)",
        "Sex offender registry",
        "Address verification",
        "Known aliases",
        "FCRA compliant"
      ],
      recommended: true,
      commission: "25% affiliate"
    },
    {
      id: "forewarn",
      name: "Forewarn (Phone Lookup)",
      price: "$30",
      turnaround: "Instant",
      features: [
        "Phone number lookup",
        "Criminal history",
        "Property ownership",
        "Financial red flags",
        "Real-time results"
      ],
      recommended: false,
      commission: "Partner rate"
    },
    {
      id: "checkr",
      name: "Checkr",
      price: "$40",
      turnaround: "Real-time - 48hrs",
      features: [
        "Criminal database search",
        "Continuous monitoring",
        "Identity verification",
        "Federal/state/county records",
        "API integration"
      ],
      recommended: false,
      commission: "Reseller terms"
    }
  ];

  const requestCheck = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/background-check/request`,
        {
          buyer_user_id: buyerInfo.user_id,
          listing_id: listingId,
          provider: selectedProvider
        },
        { withCredentials: true }
      );
      
      setResult(response.data);
      toast.success("Background check initiated!");
    } catch (error) {
      toast.error("Failed to initiate check");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2 text-2xl">
            <Shield className="w-6 h-6 text-green-600" />
            <span>Vet Buyer: {buyerInfo?.name}</span>
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-6">
            <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
              <h4 className="font-bold text-orange-900 mb-2 flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2" />
                Important: Legal Compliance
              </h4>
              <ul className="text-sm text-orange-800 space-y-1 ml-7">
                <li>• <strong>FCRA Compliance:</strong> You must have buyer consent and notify them of adverse decisions</li>
                <li>• <strong>Fair Housing:</strong> Use results for safety only, not blanket discrimination</li>
                <li>• <strong>Recent/Relevant:</strong> Focus on serious/recent offenses relevant to property access</li>
                <li>• By proceeding, you certify you will comply with all applicable laws</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Choose Background Check Provider</h3>
              <div className="grid md:grid-cols-3 gap-4">
                {providers.map((provider) => (
                  <Card
                    key={provider.id}
                    onClick={() => setSelectedProvider(provider.id)}
                    className={`p-6 cursor-pointer transition-all hover:shadow-lg ${
                      selectedProvider === provider.id
                        ? 'border-4 border-green-500 shadow-xl'
                        : 'border-2 border-gray-200'
                    } ${provider.recommended ? 'bg-green-50' : ''}`}
                    data-testid={`provider-${provider.id}`}
                  >
                    {provider.recommended && (
                      <div className="bg-green-600 text-white px-3 py-1 rounded-full text-xs font-bold mb-3 inline-block">
                        ⭐ RECOMMENDED
                      </div>
                    )}
                    
                    <h4 className="font-bold text-gray-900 mb-2">{provider.name}</h4>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-2xl font-bold text-green-600">{provider.price}</span>
                      <div className="flex items-center text-xs text-gray-600">
                        <Clock className="w-3 h-3 mr-1" />
                        {provider.turnaround}
                      </div>
                    </div>

                    <ul className="space-y-1 mb-3">
                      {provider.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-xs text-gray-700">
                          <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="text-xs text-gray-500 italic">{provider.commission}</div>
                  </Card>
                ))}
              </div>
            </div>

            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-5 mb-4">
              <h4 className="font-bold text-amber-900 mb-3 text-lg flex items-center">
                <DollarSign className="w-5 h-5 mr-2" />
                💡 Smart Investment in Your Safety
              </h4>
              <p className="text-base text-amber-900 font-semibold mb-2">
                You're saving <span className="text-orange-600">thousands in commissions</span> by going FSBO - don't scrimp on vetting your potential buyers!
              </p>
              <p className="text-sm text-amber-800 mb-3">
                <strong>Your safety is priceless.</strong> This is one of the services most real estate agents expense behind the scenes on your behalf as part of earning their commission.
              </p>
              <div className="bg-white rounded-lg p-3 border border-amber-200">
                <p className="text-xs text-gray-700 italic">
                  <strong>Industry Insight:</strong> Professional agents routinely vet buyers before showings to protect their clients. By handling your own sale, you're responsible for this crucial safety step. A $30-40 background check is a small price compared to the $15,000+ commission you're saving.
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">🛡️ Why Professional Agents Use Background Checks</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• <strong>Safety First:</strong> Know who's entering your home during showings</li>
                <li>• <strong>Peace of Mind:</strong> Verify buyer identity and criminal history</li>
                <li>• <strong>Serious Buyers Only:</strong> Background check requirement filters out non-serious inquiries</li>
                <li>• <strong>Fast Results:</strong> Most checks complete within hours</li>
                <li>• <strong>Legal Protection:</strong> Document due diligence for your safety</li>
              </ul>
            </div>

            <div className="flex space-x-4">
              <Button
                onClick={onClose}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={requestCheck}
                disabled={loading}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                data-testid="initiate-check-btn"
              >
                {loading ? "Processing..." : `Proceed with ${providers.find(p => p.id === selectedProvider)?.name}`}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 text-center">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-600" />
              <h3 className="text-2xl font-bold text-green-900 mb-2">Background Check Initiated!</h3>
              <p className="text-green-700">Check ID: {result.check_id}</p>
            </div>

            <Card className="p-6">
              <h4 className="font-bold text-gray-900 mb-4">Next Steps:</h4>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Complete the check</p>
                    <p className="text-sm text-gray-600">Click the link below to {result.provider_info.name}'s secure portal</p>
                    <a
                      href={result.provider_info.affiliate_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 mt-2 text-blue-600 hover:underline"
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span>Open {result.provider_info.name}</span>
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Provide buyer information</p>
                    <p className="text-sm text-gray-600">
                      Buyer Name: <strong>{result.buyer_info.name}</strong><br />
                      Buyer Email: <strong>{result.buyer_info.email}</strong>
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-bold">3</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Review results</p>
                    <p className="text-sm text-gray-600">
                      Turnaround: {result.provider_info.turnaround}<br />
                      Includes: {result.provider_info.includes}
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-sm text-orange-800">{result.compliance_notice}</p>
            </div>

            <Button onClick={onClose} className="w-full">
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VetBuyerDialog;
